import { useCallback } from 'react';
import { LocationData, Server, GameNotification } from '../types';
import {
  SERVER_TYPES,
  SERVER_FAILURE_PROBABILITY_PER_TICK,
  SERVER_REPAIR_BASE_COST_MULTIPLIER,
  SERVER_REPAIR_TICKS,
} from '../constants';

interface FacilitySafetyLite {
  repairTimeReduction: number;
  failureReduction: number;
}

interface UseHardwareFailuresProps {
  locations: Record<string, LocationData>;
  setLocations: React.Dispatch<React.SetStateAction<Record<string, LocationData>>>;
  money: number;
  setMoney: React.Dispatch<React.SetStateAction<number>>;
  gameTime: number;
  isPaused: boolean;
  currentFloor: string | null;
  upgrades: { disasterPreparedness: number; [key: string]: number };
  technicianCount?: number;
  facilitySafetyByLocation?: Record<string, FacilitySafetyLite>;
  triggerNotification: (message: string, type: GameNotification['type'], duration?: number) => void;
}

interface HardwareFailuresHook {
  checkForFailures: (currentGameTime: number) => void;
  repairServer: (locationId: string, rackId: number, serverId: string) => boolean;
}

export const useHardwareFailures = ({
  locations,
  setLocations,
  money,
  setMoney,
  gameTime,
  isPaused,
  currentFloor,
  upgrades,
  technicianCount = 0,
  facilitySafetyByLocation = {},
  triggerNotification,
}: UseHardwareFailuresProps): HardwareFailuresHook => {

  // Roll failures and complete in-progress repairs. Called from the game loop.
  const checkForFailures = useCallback((currentGameTime: number) => {
    if (isPaused) return;

    // Disaster preparedness reduces failure probability; security officers handled upstream
    const reduction = Math.min(0.9, (upgrades.disasterPreparedness || 0) * 0.1);
    const baseFailureProb = SERVER_FAILURE_PROBABILITY_PER_TICK * (1 - reduction);

    // Technicians reduce repair time (30% per technician, max 90%)
    const baseRepairTimeMultiplier = Math.max(0.1, 1 - technicianCount * 0.30);
    // Technicians auto-repair failed servers every ~5 min (300 ticks). One server per technician per check.
    const autoRepairThisTick = technicianCount > 0 && currentGameTime % 300 === 0;

    const newlyFailed: string[] = [];
    const repaired: string[] = [];
    const autoRepaired: string[] = [];
    let changed = false;

    setLocations(prev => {
      const next: Record<string, LocationData> = {};
      Object.entries(prev).forEach(([locId, location]) => {
        if (!location.unlocked || !location.floors) {
          next[locId] = location;
          return;
        }
        // Per-location facility safety: bins lower failure odds, cabinets speed repairs
        const facility = facilitySafetyByLocation[locId];
        const failureProb = baseFailureProb * (1 - (facility?.failureReduction ?? 0));
        const repairTimeMultiplier = Math.max(0.1, baseRepairTimeMultiplier * (1 - (facility?.repairTimeReduction ?? 0)));
        let locChanged = false;
        const updatedFloors: Record<string, typeof location.floors[string]> = {};

        // Count auto-repair slots used this tick across this location (one per technician)
        let autoRepairSlotsUsed = 0;

        Object.entries(location.floors).forEach(([floorId, floor]) => {
          let floorChanged = false;
          const updatedRacks = floor.racks.map(rack => {
            let rackChanged = false;
            // Out-of-band management: technicians can only remotely service racks with a mgmt switch
            const rackManaged = !!rack.managementSwitch;
            const updatedServers = rack.servers.map(server => {
              const status = server.failureStatus ?? 'operational';
              // Complete a repair that has finished
              if (status === 'repairing' && server.repairCompletesAt !== undefined && currentGameTime >= server.repairCompletesAt) {
                rackChanged = true;
                const st = SERVER_TYPES.find(s => s.id === server.type);
                repaired.push(st?.name ?? 'Server');
                return { ...server, failureStatus: 'operational' as const, repairCompletesAt: undefined };
              }
              // Technician auto-repair: only on remotely-managed racks (needs OOB/iDRAC)
              if (autoRepairThisTick && rackManaged && status === 'failed' && autoRepairSlotsUsed < technicianCount) {
                autoRepairSlotsUsed++;
                rackChanged = true;
                const st = SERVER_TYPES.find(s => s.id === server.type);
                autoRepaired.push(st?.name ?? 'Server');
                const completesAt = currentGameTime + Math.round(SERVER_REPAIR_TICKS * repairTimeMultiplier);
                return { ...server, failureStatus: 'repairing' as const, repairCompletesAt: completesAt };
              }
              // Roll a new failure for operational servers
              if (status === 'operational' && Math.random() < failureProb) {
                rackChanged = true;
                const st = SERVER_TYPES.find(s => s.id === server.type);
                newlyFailed.push(`${st?.name ?? 'Server'} (${location.name})`);
                return { ...server, failureStatus: 'failed' as const };
              }
              return server;
            });
            if (rackChanged) {
              floorChanged = true;
              return { ...rack, servers: updatedServers };
            }
            return rack;
          });
          if (floorChanged) {
            locChanged = true;
            updatedFloors[floorId] = { ...floor, racks: updatedRacks };
          } else {
            updatedFloors[floorId] = floor;
          }
        });
        if (locChanged) {
          changed = true;
          next[locId] = { ...location, floors: updatedFloors };
        } else {
          next[locId] = location;
        }
      });
      return changed ? next : prev;
    });

    newlyFailed.forEach(name => triggerNotification(`Hardware failure: ${name} has gone offline!`, 'error', 8000));
    repaired.forEach(name => triggerNotification(`${name} repaired and back online.`, 'success', 5000));
    if (autoRepaired.length > 0) {
      triggerNotification(`Technician auto-repairing ${autoRepaired.length} server(s).`, 'info', 4000);
    }
  }, [isPaused, upgrades.disasterPreparedness, technicianCount, facilitySafetyByLocation, setLocations, triggerNotification]);

  // Pay to repair a failed server.
  const repairServer = useCallback((locationId: string, rackId: number, serverId: string): boolean => {
    const location = locations[locationId];
    if (!location || !currentFloor) return false;
    const floor = location.floors?.[currentFloor];
    if (!floor) return false;
    const rack = floor.racks.find(r => r.id === rackId);
    if (!rack) return false;
    const server = rack.servers.find(s => s.id === serverId);
    if (!server) return false;
    if (server.failureStatus !== 'failed') return false;

    const st = SERVER_TYPES.find(s => s.id === server.type);
    const cost = Math.round((st?.cost ?? 0) * SERVER_REPAIR_BASE_COST_MULTIPLIER);
    if (money < cost) {
      triggerNotification(`Not enough money to repair (needs $${cost.toLocaleString()})`, 'error');
      return false;
    }

    // Technicians + spare-parts cabinets reduce repair time.
    // No management switch on the rack → on-site dispatch, repairs take ~80% longer.
    const facility = facilitySafetyByLocation[locationId];
    const noOobPenalty = rack.managementSwitch ? 1 : 1.8;
    const repairTimeMultiplier = Math.max(0.1, (1 - technicianCount * 0.30) * (1 - (facility?.repairTimeReduction ?? 0)) * noOobPenalty);
    const completesAt = gameTime + Math.round(SERVER_REPAIR_TICKS * repairTimeMultiplier);

    setMoney(prev => prev - cost);
    setLocations(prev => {
      const loc = prev[locationId];
      if (!loc) return prev;
      const updatedFloor = { ...loc.floors[currentFloor] };
      updatedFloor.racks = updatedFloor.racks.map(r => {
        if (r.id !== rackId) return r;
        return {
          ...r,
          servers: r.servers.map((s: Server) =>
            s.id === serverId
              ? { ...s, failureStatus: 'repairing' as const, repairCompletesAt: completesAt }
              : s
          ),
        };
      });
      return { ...prev, [locationId]: { ...loc, floors: { ...loc.floors, [currentFloor]: updatedFloor } } };
    });

    const oobNote = rack.managementSwitch ? '' : ' — no mgmt switch, on-site dispatch (slower)';
    const timeNote = technicianCount > 0 ? ` (${Math.round(SERVER_REPAIR_TICKS * repairTimeMultiplier)}t)` : '';
    triggerNotification(`${st?.name ?? 'Server'} repair started ($${cost.toLocaleString()})${timeNote}${oobNote}.`, 'info');
    return true;
  }, [locations, currentFloor, money, gameTime, technicianCount, facilitySafetyByLocation, setMoney, setLocations, triggerNotification]);

  return { checkForFailures, repairServer };
};
